import { useRef } from "react";
import { useLocale } from "../i18n/useLocale";
import { useFocusOnMount } from "../ui/useFocusOnMount";
import { buildMaterialSummary } from "./materialSummary";
import { formatMaterialLabel, summarizeMaterialPlan } from "./materialLabels";

function formatPercent(value, localeTag) {
  return new Intl.NumberFormat(localeTag, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  }).format(value);
}

export function MaterialSummaryView({ project, layout, onClose }) {
  const { display } = useLocale();
  const backRef = useRef(null);
  const summary = buildMaterialSummary(project, layout);
  const exportDate = new Date().toLocaleDateString(display.localeTag);
  const { sourcePanel, metrics } = summary;
  const sheetSize = display.formatSheet(sourcePanel.length, sourcePanel.width);

  useFocusOnMount(backRef);

  return (
    <div className="export-page material-summary-page">
      <a className="skip-link" href="#export-main">{display.t("web.shell.skipLink")}</a>
      <div className="export-toolbar no-print">
        <button
          ref={backRef}
          type="button"
          className="secondary-button export-toolbar-back"
          onClick={onClose}
        >
          {display.t("web.werkplan.backToApp")}
        </button>
        <div className="export-toolbar-actions">
          <button type="button" className="primary-button" onClick={() => window.print()}>
            {display.t("web.werkplan.printPdf")}
          </button>
        </div>
      </div>

      <main id="export-main" tabIndex={-1}>
      <header className="export-header">
        <div>
          <p className="export-kicker">{display.t("web.materialSummary.kicker")}</p>
          <h1>{display.t("web.materialSummary.title")}</h1>
          <p className="export-subtitle print-only">
            {summary.projectName} · {exportDate}
          </p>
          <p className="export-header-mobile-summary no-print">
            <strong>{summary.projectName}</strong>
            {" · "}
            {summarizeMaterialPlan(project.materialName, layout.breakdown)}
            {" · "}
            {sheetSize}
          </p>
        </div>
      </header>

      <section className="material-summary-intro">
        <p>{display.t("web.materialSummary.intro")}</p>
      </section>

      <section className="material-summary-hero">
        <div className="material-summary-stat">
          <strong>{metrics.newSheetCount}</strong>
          <span>
            {display.t(
              metrics.newSheetCount === 1
                ? "web.materialSummary.stats.newSheet"
                : "web.materialSummary.stats.newSheets",
            )}
          </span>
        </div>
        {metrics.restSheetCount > 0 && (
          <div className="material-summary-stat">
            <strong>{metrics.restSheetCount}</strong>
            <span>{display.t("web.materialSummary.stats.fromRestStock")}</span>
          </div>
        )}
        <div className="material-summary-stat">
          <strong>{summary.activePartCount}</strong>
          <span>{display.t("web.materialSummary.stats.parts")}</span>
        </div>
        <div className="material-summary-stat">
          <strong>{formatPercent(metrics.utilisation, display.localeTag)}%</strong>
          <span>{display.t("web.materialSummary.stats.utilisation")}</span>
        </div>
      </section>

      {summary.invalidCount > 0 && (
        <p className="material-summary-warning" role="alert">
          {display.t(
            summary.invalidCount === 1
              ? "web.materialSummary.invalidSingular"
              : "web.materialSummary.invalidPlural",
            { count: summary.invalidCount },
          )}
        </p>
      )}

      <section className="export-section">
        <h2>{display.t("web.materialSummary.material.title")}</h2>
        <dl className="material-summary-facts">
          <div>
            <dt>{display.t("web.materialSummary.material.plan")}</dt>
            <dd>{summary.projectName}</dd>
          </div>
          <div>
            <dt>{display.t("web.materialSummary.material.material")}</dt>
            <dd>{summarizeMaterialPlan(summary.materialName, summary.breakdown)}</dd>
          </div>
          <div>
            <dt>{display.t("web.materialSummary.material.standardSheet")}</dt>
            <dd>{sheetSize}</dd>
          </div>
          <div>
            <dt>{display.t("web.materialSummary.material.kerf")}</dt>
            <dd>{display.formatLength(sourcePanel.kerf)}</dd>
          </div>
          <div>
            <dt>{display.t("web.materialSummary.material.margin")}</dt>
            <dd>{display.formatLength(sourcePanel.margin)}</dd>
          </div>
        </dl>
      </section>

      <section className="export-section">
        <h2>{display.t("web.materialSummary.order.title")}</h2>
        {summary.breakdown.length === 0 ? (
          <p>{display.t("web.materialSummary.order.empty")}</p>
        ) : (
          <table className="export-table material-summary-table">
            <thead>
              <tr>
                <th>{display.t("web.materialSummary.order.thickness")}</th>
                <th>{display.t("web.materialSummary.order.newSheets")}</th>
                <th>{display.t("web.materialSummary.order.restStock")}</th>
                <th>{display.t("web.materialSummary.order.parts")}</th>
                <th>{display.t("web.materialSummary.order.sheetSize")}</th>
              </tr>
            </thead>
            <tbody>
              {summary.breakdown.map((row) => (
                <tr key={row.thickness}>
                  <td>{display.formatThickness(row.thickness)}</td>
                  <td><strong>{row.newSheetCount ?? row.sheetCount}×</strong></td>
                  <td>{row.restSheetCount || 0}×</td>
                  <td>{display.t("web.materialSummary.order.partsCount", { count: row.partCount })}</td>
                  <td>{sheetSize}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {summary.breakdown.length > 0 && (
          <p className="material-summary-order-line">
            {display.t("web.materialSummary.order.orderPrefix")}{" "}
            <strong>
              {summary.breakdown.map((row) => display.t("web.materialSummary.order.orderItem", {
                count: row.newSheetCount ?? row.sheetCount,
                material: formatMaterialLabel(summary.materialName, row.thickness),
                size: sheetSize,
              })).join(" · ")}
            </strong>
          </p>
        )}
        {metrics.restSheetCount > 0 && (
          <p className="material-summary-order-line">
            {display.t("web.materialSummary.order.restStockLine")}{" "}
            <strong>
              {display.t(
                metrics.restSheetCount === 1
                  ? "web.materialSummary.order.restStockSingular"
                  : "web.materialSummary.order.restStockPlural",
                { count: metrics.restSheetCount },
              )}
            </strong>
          </p>
        )}
      </section>

      <section className="export-section">
        <h2>{display.t("web.materialSummary.subprojects.title")}</h2>
        <ul className="material-summary-projects">
          {summary.projects.map((subproject) => (
            <li key={subproject.id}>
              <span className="material-summary-chip" style={{ background: subproject.color }} aria-hidden="true" />
              <strong>{subproject.name || display.t("web.panels.unnamedProject")}</strong>
              <span>{display.t("web.materialSummary.subprojects.parts", { count: subproject.partCount })}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="export-section print-only">
        <h2>{display.t("web.materialSummary.tip.title")}</h2>
        <p>{display.t("web.materialSummary.tip.body")}</p>
      </section>
      </main>
    </div>
  );
}
